import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Check, Clock, AlertTriangle, XCircle, Zap, HardHat, Loader2, MousePointerClick, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { prospectsAPI } from '@/lib/api';
import { cn } from '@/lib/utils';

// Étapes Courtage bloquées tant que le mandat RGPD n'est pas réalisé
const EP_BLOCKED_WITHOUT_RGPD = [
    'ep_etude_recue', 'ep_etude_expliquee', 'ep_rib_receptionne',
    'ep_contrat_envoye', 'ep_contrat_explique', 'ep_gagne', 'ep_questionnaire_chantier',
];

const EP_STAGES = [
    { id: 'ep_debut_prospection', name: 'Prospection' },
    { id: 'ep_facture_recue', name: 'Facture' },
    { id: 'ep_mandat_envoye', name: 'Mandat envoyé' },
    { id: 'ep_mandat_signe', name: 'Mandat signé' },
    { id: 'ep_dossier_transmis', name: 'Transmis' },
    { id: 'ep_etude_recue', name: 'Étude reçue' },
    { id: 'ep_etude_expliquee', name: 'Étude expliquée' },
    { id: 'ep_rib_receptionne', name: 'RIB' },
    { id: 'ep_contrat_envoye', name: 'Contrat envoyé' },
    { id: 'ep_contrat_explique', name: 'Attente signature' },
    { id: 'ep_gagne', name: 'Gagné' },
    { id: 'ep_questionnaire_chantier', name: 'Formulaire' },
];
const CH_STAGES = [
    { id: 'ch_formulaire_rempli', name: 'Formulaire' },
    { id: 'ch_visite_planifiee', name: 'Visite planifiée' },
    { id: 'ch_visite_effectuee', name: 'Visite faite' },
    { id: 'ch_dossier_etude', name: "À l'étude" },
    { id: 'ch_etude_presentee', name: 'Étude présentée' },
    { id: 'ch_devis_envoye', name: 'Devis envoyé' },
    { id: 'ch_devis_accepte', name: 'Devis accepté' },
    { id: 'ch_planification', name: 'Planification' },
    { id: 'ch_en_cours', name: 'En cours' },
    { id: 'ch_termine', name: 'Terminé' },
    { id: 'ch_attente_paiement', name: 'Attente paiement' },
    { id: 'ch_paiement_effectue', name: 'Payé' },
    { id: 'ch_valide', name: 'Validé' },
];

const FINAL_STAGES = ['ep_gagne', 'ep_questionnaire_chantier', 'ep_perdu', 'ch_valide', 'ch_perdu', 'ch_paiement_effectue'];
// Travaux d’optimisation : étapes qui ne doivent JAMAIS être "en retard" — du devis accepté/signé
// jusqu'à gagné inclus (en attente d'installation + déjà gagné). Aligné avec le backend.
const CH_NOT_OVERDUE_STAGES = [
    'ch_devis_accepte', 'ch_devis_signe', 'ch_dossier_subvention', 'ch_subvention_obtenue',
    'ch_planification', 'ch_en_cours', 'ch_chantier_en_cours',
    'ch_termine', 'ch_chantier_termine', 'ch_attente_paiement', 'ch_paiement_effectue',
    'ch_valide', 'ch_gagne',
];
const OVERDUE_DAYS = 10;

const daysSince = (iso) => {
    if (!iso) return null;
    const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    return Number.isFinite(d) ? d : null;
};

const fmtShort = (iso) => {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
        return null;
    }
};

export function StageProgressBar({ prospect, pipelineType, onUpdate, readOnly = false }) {
    const stages = pipelineType === 'easy_power' ? EP_STAGES : CH_STAGES;
    const currentStage = prospect[`${pipelineType}_stage`];
    const lostStage = pipelineType === 'easy_power' ? 'ep_perdu' : 'ch_perdu';
    const isLost = currentStage === lostStage;
    const currentIndex = stages.findIndex((s) => s.id === currentStage);

    const changedAt = prospect[`${pipelineType}_stage_changed_at`];
    const daysInStage = daysSince(changedAt);
    const daysSinceCreation = daysSince(prospect.created_at);
    const daysInactive = daysSince(prospect.updated_at);
    const isFinal = FINAL_STAGES.includes(currentStage);
    // Un chantier "en attente d'installation" (ou déjà gagné) n'est jamais "en retard".
    const isChantierExempt = pipelineType !== 'easy_power' && CH_NOT_OVERDUE_STAGES.includes(currentStage);
    const overdue = !isFinal && !isLost && !isChantierExempt && daysInactive != null && daysInactive >= OVERDUE_DAYS;
    const overdueDays = overdue ? daysInactive - OVERDUE_DAYS : null;

    const Icon = pipelineType === 'easy_power' ? Zap : HardHat;
    const accent = pipelineType === 'easy_power' ? 'purple' : 'amber';
    const currentName = isLost ? 'Affaire perdue' : (stages[currentIndex]?.name || '—');

    // Déplacement manuel de l'étape depuis le dossier
    const [pending, setPending] = useState(null); // étape ciblée en attente de confirmation
    const [changing, setChanging] = useState(false);
    const isRgpdRealise = prospect.rgpd_mandat?.realise === true;

    const isBlockedByRgpd = (stageId) =>
        pipelineType === 'easy_power' && !isRgpdRealise && EP_BLOCKED_WITHOUT_RGPD.includes(stageId);

    const handleStageClick = (stage) => {
        if (readOnly) return;
        if (stage.id === currentStage) return;
        if (isBlockedByRgpd(stage.id)) {
            toast.error('Mandat RGPD obligatoire avant de passer à cette étape');
            return;
        }
        setPending(stage);
    };

    const confirmChange = async () => {
        if (!pending) return;
        setChanging(true);
        try {
            await prospectsAPI.changeStage(prospect.id, pipelineType, pending.id, null);
            toast.success(`Étape déplacée vers « ${pending.name} »`);
            setPending(null);
            onUpdate?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Erreur lors du changement d'étape");
        } finally {
            setChanging(false);
        }
    };

    return (
        <Card data-testid={`stage-progress-${pipelineType}`} className={cn('border', isLost ? 'border-red-200' : 'border-slate-200')}>
            <CardContent className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                        <Icon className={cn('w-4 h-4', accent === 'purple' ? 'text-purple-600' : 'text-amber-600')} />
                        <span className="text-sm font-semibold text-slate-800">
                            {pipelineType === 'easy_power' ? 'Courtage' : 'Travaux d’optimisation'} — étape : <span className={isLost ? 'text-red-600' : 'text-slate-900'}>{currentName}</span>
                        </span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {prospect.created_at && (
                            <Badge variant="outline" className="gap-1 text-slate-600" data-testid={`created-at-badge-${pipelineType}`}>
                                <CalendarDays className="w-3 h-3" /> Créé le {fmtShort(prospect.created_at)}
                                {daysSinceCreation != null && ` · il y a ${daysSinceCreation} j`}
                            </Badge>
                        )}
                        {!isLost && daysInStage != null && (
                            <Badge variant="outline" className="gap-1 text-slate-600" data-testid={`stage-since-badge-${pipelineType}`}>
                                <Clock className="w-3 h-3" /> Sur cette étape depuis {daysInStage} j
                                {changedAt && ` (${fmtShort(changedAt)})`}
                            </Badge>
                        )}
                        {overdue && (
                            <Badge className="gap-1 bg-amber-100 text-amber-700 hover:opacity-90" data-testid={`overdue-badge-${pipelineType}`}>
                                <AlertTriangle className="w-3 h-3" /> En retard de {overdueDays} j
                            </Badge>
                        )}
                    </div>
                </div>

                {isLost ? (
                    <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                        <XCircle className="w-4 h-4" />
                        Ce dossier est perdu{prospect.loss_reason || prospect.auto_lost_reason ? ` — ${prospect.loss_reason || prospect.auto_lost_reason}` : ''}.
                    </div>
                ) : (
                    <>
                        <p className="flex items-center gap-1.5 text-[11px] text-slate-400 mb-2">
                            <MousePointerClick className="w-3 h-3" /> Cliquez sur une étape pour déplacer le dossier
                        </p>
                        <div className="flex items-stretch gap-0 overflow-x-auto pb-2">
                            {stages.map((s, i) => {
                                const done = currentIndex >= 0 && i < currentIndex;
                                const active = i === currentIndex;
                                const blocked = isBlockedByRgpd(s.id);
                                return (
                                    <button
                                        type="button"
                                        key={s.id}
                                        onClick={() => handleStageClick(s)}
                                        disabled={active || changing || readOnly}
                                        data-testid={`stage-step-${pipelineType}-${s.id}`}
                                        title={blocked ? 'Mandat RGPD requis' : (active ? 'Étape actuelle' : `Déplacer vers « ${s.name} »`)}
                                        className={cn(
                                            'group flex flex-col items-center min-w-[68px] relative rounded-md px-0.5 pt-0.5 transition-colors',
                                            active ? 'cursor-default' : 'cursor-pointer hover:bg-slate-50',
                                            blocked && 'opacity-50',
                                        )}
                                    >
                                        {/* connector */}
                                        {i > 0 && (
                                            <span className={cn('absolute top-3 right-1/2 h-0.5 w-full', (done || active) ? (accent === 'purple' ? 'bg-purple-400' : 'bg-amber-400') : 'bg-slate-200')} />
                                        )}
                                        <div className={cn(
                                            'relative z-10 flex h-6 w-6 items-center justify-center rounded-full border-2 text-[10px] font-bold transition-all',
                                            done && (accent === 'purple' ? 'bg-purple-600 border-purple-600 text-white' : 'bg-amber-500 border-amber-500 text-white'),
                                            active && (accent === 'purple' ? 'bg-white border-purple-600 text-purple-700 ring-2 ring-purple-200' : 'bg-white border-amber-500 text-amber-600 ring-2 ring-amber-200'),
                                            !done && !active && 'bg-white border-slate-300 text-slate-400 group-hover:border-slate-400',
                                        )}>
                                            {done ? <Check className="w-3 h-3" /> : i + 1}
                                        </div>
                                        <span className={cn('mt-1 text-[10px] text-center leading-tight px-0.5',
                                            active ? 'font-semibold text-slate-800' : done ? 'text-slate-500' : 'text-slate-400')}>
                                            {s.name}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </>
                )}
            </CardContent>

            <AlertDialog open={!!pending} onOpenChange={(o) => { if (!o) setPending(null); }}>
                <AlertDialogContent data-testid="stage-move-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Déplacer l'étape du dossier</AlertDialogTitle>
                        <AlertDialogDescription>
                            Confirmer le passage de <strong>{currentName}</strong> vers <strong>{pending?.name}</strong> pour le pipeline {pipelineType === 'easy_power' ? 'Courtage' : 'Travaux d’optimisation'} ?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={changing}>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmChange(); }} disabled={changing} data-testid="confirm-stage-move-btn">
                            {changing && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                            Confirmer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Card>
    );
}

export default StageProgressBar;
